import { Badge, Banner, Button, Checkbox, Row, icons } from '@ui';
import { useEffect, useState } from 'react';
import { ApiError } from '../api';
import { ChevronIcon } from '../icons';
import { RowMenu, useConfirm, type RowAction } from '../jui';
import { bugApi, type TrackerVersion, type TrackerVersionBatchAction, type TrackerVersionState } from '../bugApi';

// «Трекер → Версии» (Вк2, tracker_versions Вр2–Вр4, Вр7): the catalog of tracker builds — shared by all projects, so it stands in
// the shared area, not in a project's «Возможности». Projects on each version by the load log, the marks, the batch actions, deletion.

export const OLD_MARK: Record<'deprecated' | 'doomed', { label: string; tone: 'warning' | 'error' }> = {
  deprecated: { label: 'Устарела', tone: 'warning' },
  doomed: { label: 'К удалению', tone: 'error' },
};
export const reason = (err: unknown, fallback: string): string => (err instanceof ApiError && err.message ? err.message : fallback);

/** The marks of a version: «Последняя», the old-state badge and — in the catalog only — how many projects are on it. */
export function VersionMarks({ v, withCount = false }: { v: TrackerVersion; withCount?: boolean }) {
  return (
    <>
      {v.latest && <Badge tone="accent">Последняя (автоматически)</Badge>}
      {v.state !== 'active' && (
        <Badge tone={OLD_MARK[v.state].tone} icon={<icons.IconAlert />}>
          {OLD_MARK[v.state].label}
        </Badge>
      )}
      {withCount && v.projects.length > 0 && (
        <span className="bug-version-count" title="Сколько сайтов загрузили эту версию">
          {`${v.projects.length} пр.`}
        </span>
      )}
    </>
  );
}

export function TrackerVersions() {
  const [versions, setVersions] = useState<TrackerVersion[] | null>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [oldOpen, setOldOpen] = useState(false);
  /** Вр7: versions ticked for one action over all of them. */
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const { confirm, dialog: confirmDialog } = useConfirm();

  useEffect(() => {
    bugApi
      .trackerVersions()
      .then((res) => setVersions(res.versions))
      .catch((err) => setError(reason(err, 'Версии не загрузились — повторите.')));
  }, []);

  const act = async (run: () => Promise<{ versions: TrackerVersion[] }>) => {
    setError('');
    setNotice('');
    try {
      const next = (await run()).versions;
      setVersions(next);
      setPicked((prev) => new Set([...prev].filter((v) => next.some((item) => item.version === v))));
    } catch (err) {
      setError(reason(err, 'Не получилось — повторите.'));
    }
  };
  const mark = (v: string, state: TrackerVersionState) => void act(() => bugApi.markTrackerVersion(v, state));
  // Вр7: deleting asks the person first, one version or many.
  const askDelete = (list: string[]) =>
    confirm({
      title: list.length === 1 ? `Удалить версию ${list[0]}?` : `Удалить версии (${list.length})?`,
      text: list.length === 1 ? 'Сборка пропадёт, вернуть нельзя.' : `${list.join(', ')}. Сборки пропадут, вернуть нельзя.`,
      confirmLabel: 'Удалить',
    });
  const remove = async (v: string) => {
    if (await askDelete([v])) void act(() => bugApi.deleteTrackerVersion(v));
  };
  /** Вр7: one action over the ticked versions; what the service left is named with the reason. */
  const batch = async (action: TrackerVersionBatchAction) => {
    const list = [...picked];
    if (list.length === 0) return;
    if (action === 'delete' && !(await askDelete(list))) return;
    await act(async () => {
      const res = await bugApi.batchTrackerVersions(list, action, action === 'delete' ? true : undefined);
      setPicked(new Set());
      if (res.skipped.length > 0) setNotice(`Не тронуты: ${res.skipped.map((item) => `${item.version} — ${item.reason}`).join('; ')}`);
      return res;
    });
  };
  const pick = (v: string, on: boolean) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (on) next.add(v);
      else next.delete(v);
      return next;
    });

  const actionsOf = (v: TrackerVersion): RowAction[] => [
    ...(v.state === 'active' && !v.latest ? [{ label: 'Устарела', run: () => mark(v.version, 'deprecated') }] : []),
    ...(v.state !== 'doomed' && !v.latest ? [{ label: 'К удалению', run: () => mark(v.version, 'doomed') }] : []),
    ...(v.state !== 'active' ? [{ label: 'Вернуть', run: () => mark(v.version, 'active') }] : []),
    ...(v.state === 'doomed' ? [{ label: 'Удалить', run: () => void remove(v.version) }] : []),
  ];
  // Each version once: the label, the tick for the batch and its «⋯» menu in one row. The latest version has no tick (it is not retired or deleted).
  const versionRow = (v: TrackerVersion) => (
    <div key={v.version} className="bug-version-row" data-version={v.version}>
      <span className="bug-version-pick-label">
        <span className="bug-version-label">
          <span className="mono">{v.version}</span>
          <VersionMarks v={v} withCount />
        </span>
      </span>
      {!v.latest && <Checkbox className="bug-version-tick" label={`Выбрать ${v.version}`} hideLabel checked={picked.has(v.version)} onChange={(on) => pick(v.version, on)} />}
      <RowMenu label={`Действия: ${v.version}`} actions={actionsOf(v)} />
    </div>
  );

  // Versions no site uses (and not the latest) wait in a folded group.
  const kept = (v: TrackerVersion) => v.latest || v.projects.length > 0 || picked.has(v.version);
  const olds = versions ? versions.filter((v) => !kept(v)) : [];
  const tickable = versions ? versions.filter((v) => !v.latest).map((v) => v.version) : [];
  const batchBar = picked.size > 0 && (
    <div className="bug-versions-batch" role="group" aria-label="Действия над выбранными версиями">
      <span className="bug-version-count">{`Выбрано: ${picked.size}`}</span>
      <Button variant="text" size="sm" onClick={() => void batch('deprecated')}>
        Устарели
      </Button>
      <Button variant="text" size="sm" onClick={() => void batch('doomed')}>
        К удалению
      </Button>
      <Button variant="text" size="sm" onClick={() => void batch('active')}>
        Вернуть
      </Button>
      <Button variant="danger" size="sm" onClick={() => void batch('delete')}>
        Удалить
      </Button>
      <Button variant="text" size="sm" onClick={() => setPicked(picked.size === tickable.length ? new Set() : new Set(tickable))}>
        {picked.size === tickable.length ? 'Снять' : 'Все'}
      </Button>
    </div>
  );

  return (
    <section className="bug-features-panel" aria-label="Версии трекера">
      {confirmDialog}
      {error && <Banner tone="error" title="Отказ" className="bug-version-error">{error}</Banner>}
      {versions && (
        <section className="bug-features-block bug-versions">
          {versions.length === 0 && <div className="bug-feature-hint">Версий пока нет — они появляются с выкладкой трекера.</div>}
          {batchBar}
          {notice && <Banner tone="warning" title="Часть не тронута" className="bug-version-notice">{notice}</Banner>}
          <div className="bug-versions-list">
            {versions.filter(kept).map(versionRow)}
            {olds.length > 0 && (
              <Row
                className="bug-versions-old"
                title={<b>{`Старые версии (${olds.length})`}</b>}
                meta={<ChevronIcon open={oldOpen} />}
                expanded={oldOpen}
                onClick={() => setOldOpen(!oldOpen)}
              >
                {oldOpen && <div className="bug-versions-list">{olds.map(versionRow)}</div>}
              </Row>
            )}
          </div>
        </section>
      )}
    </section>
  );
}
